import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    FormLabelProps,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { InventoryBin } from '../../types/inventory';
import { useSearchInventoryBins } from '../../hooks/inventory/useSearchInventoryBins';
import {
    CheckboxMenuList,
    CheckboxMenuRow,
    CheckboxMenuSearch,
    CheckboxMenuTrigger,
    checkboxMenuColors,
    checkboxMenuPopoverContentSx,
} from '../../ui';

const NEW_BIN_VALUE = '__new_bin__';
const NEW_BIN_DRAFT_PREFIX = 'new:';

export interface InventoryBinsFieldProps {
    value: Array<InventoryBin>;
    onChange: (bins: Array<InventoryBin>) => void;
    /** When set, search/create are scoped to this location. Required for editing. */
    locationId?: string | null;
    /**
     * Multi-location search scope for filters (overrides locationId when provided).
     * Use feed `locationIds` including `'all'`.
     */
    locationIds?: Array<string> | 'all' | null;
    /** When false, hide "+ Create new bin" (e.g. feed filters). Default true. */
    allowCreate?: boolean;
    isDisabled?: boolean;
    label?: string;
    labelProps?: FormLabelProps;
    placeholder?: string;
    /** `sm` matches the 2rem form controls (size="sm" inputs and dropdowns). */
    size?: 'sm' | 'md';
}

const InventoryBinsField = ({
    value,
    onChange,
    locationId = null,
    locationIds = null,
    allowCreate = true,
    isDisabled = false,
    label = 'Bins',
    labelProps,
    placeholder,
    size = 'md',
}: InventoryBinsFieldProps) => {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const searchLocationIds = useMemo((): Array<string> | 'all' | null => {
        if (locationIds === 'all') return 'all';
        if (Array.isArray(locationIds) && locationIds.length > 0) return locationIds;
        if (locationId) return [locationId];
        return null;
    }, [locationId, locationIds]);

    const canSearch = searchLocationIds != null;
    /** Import preview may create name drafts before location resolves. */
    const inputDisabled = isDisabled || (!canSearch && !allowCreate);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
        return () => clearTimeout(timer);
    }, [query]);

    const { data, isFetching } = useSearchInventoryBins(
        searchLocationIds,
        debouncedQuery,
        20,
        isMenuOpen && canSearch,
    );

    const selectedIds = useMemo(() => new Set(value.map((b) => b._id)), [value]);
    const selectedNames = useMemo(
        () => new Set(value.map((b) => b.name.trim().toLowerCase()).filter(Boolean)),
        [value],
    );

    const options = useMemo(() => {
        const bins = (data?.bins ?? []).filter(
            (b) =>
                !selectedIds.has(b._id) &&
                !selectedNames.has(b.name.trim().toLowerCase()),
        );
        const trimmed = query.trim();
        const rows: Array<{ value: string; label: string; isNew?: boolean; newName?: string }> =
            [];
        const canCreateHere =
            allowCreate &&
            trimmed &&
            !selectedNames.has(trimmed.toLowerCase());
        if (canCreateHere) {
            const exactExisting = (data?.bins ?? []).some(
                (b) => b.name.trim().toLowerCase() === trimmed.toLowerCase(),
            );
            if (!exactExisting) {
                rows.push({
                    value: NEW_BIN_VALUE,
                    label: trimmed,
                    isNew: true,
                    newName: trimmed,
                });
            }
        }
        for (const b of bins) {
            rows.push({ value: b._id, label: b.name });
        }
        return rows;
    }, [data?.bins, selectedIds, selectedNames, query, allowCreate]);

    const removeBin = (binId: string) => {
        onChange(value.filter((b) => b._id !== binId));
    };

    const selectExisting = (bin: InventoryBin) => {
        const nameKey = bin.name.trim().toLowerCase();
        if (selectedIds.has(bin._id) || selectedNames.has(nameKey)) return;
        onChange([...value, bin]);
        setQuery('');
    };

    const handleSelect = (opt: { value: string; label: string; isNew?: boolean }) => {
        if (opt.isNew || opt.value === NEW_BIN_VALUE) {
            const name = query.trim();
            if (!name) return;
            const exists = value.some(
                (b) => b.name.toLowerCase() === name.toLowerCase(),
            );
            if (!exists) {
                onChange([
                    ...value,
                    {
                        _id: `${NEW_BIN_DRAFT_PREFIX}${name}`,
                        name,
                        locationId: locationId ?? '',
                        isNew: true,
                    },
                ]);
            }
            setQuery('');
            return;
        }
        const match = data?.bins?.find((b) => b._id === opt.value);
        if (match) selectExisting(match);
    };

    const emptyHint = !canSearch && !allowCreate
        ? 'Select a location first'
        : query.trim()
          ? 'No matching bins'
          : allowCreate
            ? 'Type to search or create a bin'
            : 'Type to search bins';

    const createOption = options.find((opt) => opt.isNew);
    const searchRows = options.filter((opt) => !opt.isNew);

    const closeMenu = () => {
        setIsMenuOpen(false);
        setQuery('');
    };

    // Same trigger + popover chrome as the Dropdown primitive, so this reads
    // as one control family with the Location picker beside it. Kept custom
    // for the async bin search and the create-a-bin row.
    return (
        <FormControl>
            {label ? (
                <FormLabel fontSize="sm" {...labelProps}>
                    {label}
                </FormLabel>
            ) : null}
            <Popover
                isOpen={isMenuOpen}
                onClose={closeMenu}
                matchWidth
                placement="bottom-start"
                gutter={8}
            >
                <PopoverTrigger>
                    <Box opacity={inputDisabled ? 0.5 : undefined}>
                        <CheckboxMenuTrigger
                            isOpen={isMenuOpen}
                            tokens={value.map((bin) => bin.name)}
                            emptyLabel={
                                placeholder ??
                                (canSearch || allowCreate
                                    ? 'Search bins…'
                                    : 'Select a location first')
                            }
                            emptyLabelColor="fg.muted"
                            onRemoveToken={
                                inputDisabled
                                    ? undefined
                                    : (index: number) => {
                                          const bin = value[index];
                                          if (bin) removeBin(bin._id);
                                      }
                            }
                            onClick={
                                inputDisabled
                                    ? undefined
                                    : () => setIsMenuOpen((prev) => !prev)
                            }
                            cursor={inputDisabled ? 'not-allowed' : undefined}
                            {...(size === 'sm' ? { minH: '2rem', py: '1px' } : {})}
                        />
                    </Box>
                </PopoverTrigger>
                <Portal>
                    <PopoverContent
                        w="var(--popper-reference-width)"
                        sx={checkboxMenuPopoverContentSx}
                        data-inventory-floating-menu=""
                    >
                        <PopoverBody p={0}>
                            <CheckboxMenuSearch
                                value={query}
                                onChange={setQuery}
                                placeholder={allowCreate ? 'Search or create…' : 'Search...'}
                                autoFocus
                            />
                            <CheckboxMenuList>
                                {createOption && (
                                    <CheckboxMenuRow
                                        isSelected={false}
                                        onClick={() => handleSelect(createOption)}
                                    >
                                        <Text as="span" color="accent.fg" fontWeight="semibold">
                                            + Create “{createOption.newName}”
                                        </Text>
                                    </CheckboxMenuRow>
                                )}
                                {value.map((bin) => (
                                    <CheckboxMenuRow
                                        key={bin._id}
                                        isSelected
                                        isDisabled={inputDisabled}
                                        onClick={() => removeBin(bin._id)}
                                    >
                                        {bin.name}
                                    </CheckboxMenuRow>
                                ))}
                                {searchRows.map((opt) => (
                                    <CheckboxMenuRow
                                        key={opt.value}
                                        isSelected={false}
                                        onClick={() => handleSelect(opt)}
                                    >
                                        {opt.label}
                                    </CheckboxMenuRow>
                                ))}
                                {isFetching && searchRows.length === 0 && (
                                    <Flex align="center" justify="center" py="10px">
                                        <Spinner size="xs" />
                                    </Flex>
                                )}
                                {!isFetching &&
                                    !createOption &&
                                    searchRows.length === 0 &&
                                    value.length === 0 && (
                                        <Text
                                            fontSize="13px"
                                            color={checkboxMenuColors.muted}
                                            px="10px"
                                            py="8px"
                                        >
                                            {emptyHint}
                                        </Text>
                                    )}
                            </CheckboxMenuList>
                        </PopoverBody>
                    </PopoverContent>
                </Portal>
            </Popover>
        </FormControl>
    );
};

export default InventoryBinsField;
